'use client';
import styles from './styles.module.scss';
import ButtonLink from '../../../components/ButtonLink';
import { FunctionComponent, ReactElement, useEffect, useMemo, useState } from 'react';
import CheckRegularIcon from '../../../components/Icons/CheckRegularIcon';
import { PLANS, PricingPlan } from './pricingFeatures';
import classNames from 'classnames';
import { Tooltip } from '../../../components/Tooltip';
import { CircleCheckSolid } from '../../../components/Icons';
import { useIam } from '../../../services/IAm/hooks';
import { EducatorPlanCurrencies } from '@shared/types';
import { BookDemoButton } from '../../../app/components/BookDemoButton';

type FancyPointPropTypes = {
    children: string | ReactElement;
    iconStyle?: 'minimal' | 'primary';
};

const FancyPoint: FunctionComponent<FancyPointPropTypes> = ({
    children,
    iconStyle = 'minimal',
}) => {
    return (
        <div className={styles.fancy_point}>
            <div className={styles.icon_container}>
                {iconStyle === 'primary' ? <CircleCheckSolid /> : <CheckRegularIcon />}
            </div>

            <p>{children}</p>
        </div>
    );
};

type PricingCardsPropTypes = {
    cta?: ReactElement;
};

type PriceCardPropTypes = {
    plan: PricingPlan;
    billingPeriod: 'month' | 'year';
    currency: EducatorPlanCurrencies;
};

const PriceCard: FunctionComponent<PriceCardPropTypes> = ({ plan, billingPeriod, currency }) => {
    const { iam } = useIam();

    const callToActionLink = useMemo(() => {
        if (iam.authenticated && iam.educators.length) {
            return `${process.env.NEXT_PUBLIC_ADMIN_URL}/settings/billing`;
        }
        return `${process.env.NEXT_PUBLIC_MARKETPLACE_URL}/create-educator`;
    }, [iam]);

    const cost = useMemo(() => {
        if (!plan.pricing) {
            return 0;
        }
        const value = plan.pricing[currency][`${billingPeriod}ly`];
        if (billingPeriod === 'year') {
            return value / 12;
        }
        return value;
    }, [plan, billingPeriod, currency]);

    const costDisplay = useMemo(() => {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 0,
        }).format(cost);
    }, [cost, currency]);

    return (
        <div className={styles.price_card}>
            <h3 className={styles.card_heading}>{plan.title}</h3>
            <div className={styles.price_container}>
                <h2>
                    {costDisplay}
                    <span>/month</span>
                </h2>
            </div>
            <div className={styles.cta_container}>
                <ButtonLink
                    href={callToActionLink}
                    target="_blank"
                    preset="primary"
                    className={styles.cta_button}
                >
                    {plan.cta}
                </ButtonLink>
            </div>
            <div className={styles.fancy_points_container}>
                {plan.features.map((feature, i) => {
                    return (
                        <FancyPoint key={i}>
                            <>
                                {feature.tooltip ? (
                                    <Tooltip tooltip={feature.tooltip}>
                                        <span className={styles.tooltip_text}>{feature.label}</span>
                                    </Tooltip>
                                ) : (
                                    feature.label
                                )}
                            </>
                        </FancyPoint>
                    );
                })}
            </div>
        </div>
    );
};

const deriveCurrencyFromTimezone = (timezone: string): EducatorPlanCurrencies => {
    if (timezone === 'Europe/London') {
        return 'GBP';
    }

    const [continent] = timezone.split('/');

    switch (continent) {
        case 'Europe': {
            return 'EUR';
        }
        case 'America': {
            return 'USD';
        }
    }

    return 'USD';
};

export const PricingCards: FunctionComponent<PricingCardsPropTypes> = ({ cta }) => {
    const [billingPeriod, setBillingPeriod] = useState<'month' | 'year'>('year');
    const [currency, setCurrency] = useState<EducatorPlanCurrencies>('GBP');

    useEffect(() => {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const defaultCurrency = deriveCurrencyFromTimezone(timezone);
        setCurrency(defaultCurrency);
    }, []);

    return (
        <div>
            <div className={styles.togglesContainer}>
                <div className={styles.billing_buttons}>
                    <button
                        className={classNames(styles.billing_btn, {
                            [styles.selected]: billingPeriod === 'month',
                        })}
                        onClick={() => setBillingPeriod('month')}
                    >
                        Pay Monthly
                    </button>
                    <button
                        className={classNames(styles.billing_btn, {
                            [styles.selected]: billingPeriod === 'year',
                        })}
                        onClick={() => setBillingPeriod('year')}
                    >
                        Pay Yearly <span className={styles.saveBadge}>save 20%</span>
                    </button>
                </div>
                <div className={styles.billing_buttons}>
                    <button
                        className={classNames(styles.billing_btn, {
                            [styles.selected]: currency === 'GBP',
                        })}
                        onClick={() => setCurrency('GBP')}
                    >
                        GBP £
                    </button>
                    <button
                        className={classNames(styles.billing_btn, {
                            [styles.selected]: currency === 'USD',
                        })}
                        onClick={() => setCurrency('USD')}
                    >
                        USD $
                    </button>
                    <button
                        className={classNames(styles.billing_btn, {
                            [styles.selected]: currency === 'EUR',
                        })}
                        onClick={() => setCurrency('EUR')}
                    >
                        EUR €
                    </button>
                </div>
            </div>
            <div className={styles.cards_container}>
                {Object.entries(PLANS).map(([planName, plan]) => {
                    return (
                        <PriceCard
                            key={planName}
                            plan={plan}
                            billingPeriod={billingPeriod}
                            currency={currency}
                        />
                    );
                })}
            </div>
            <div>
                <div className={styles.enterpriseBanner}>
                    <div className={styles.ctaContainer}>
                        <h3>CUSTOM & ENTERPRISE</h3>
                        <p>
                            Looking for a more bespoke plan for your organization? Book a demo with
                            our team to discuss your requirements.
                        </p>
                        <BookDemoButton />
                    </div>
                    <div className={styles.enterpriseFeaturesList}>
                        <FancyPoint iconStyle="primary">Single Sign On (SSO)</FancyPoint>
                        <FancyPoint iconStyle="primary">Advanced Security</FancyPoint>
                        <FancyPoint iconStyle="primary">
                            Custom Service Level Agreements (SLAs)
                        </FancyPoint>
                        <FancyPoint iconStyle="primary">99.99% Uptime Guarantee</FancyPoint>
                        <FancyPoint iconStyle="primary">
                            Dedicated Customer Success Manager
                        </FancyPoint>
                        <FancyPoint iconStyle="primary">Custom Features</FancyPoint>
                        <FancyPoint iconStyle="primary">Onboarding & Training</FancyPoint>
                        <FancyPoint iconStyle="primary">Implementation Support</FancyPoint>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PricingCards;
